import { ShareBaseDocument } from 'phoenix/domain';
import { IDocumentService } from 'sharebase/services/documentService';
import { SortParameter } from 'sharebase/filters/sortContent';

interface RecentDocument {
	Embedded: {
		Document:ShareBaseDocument
	},
	LastActivityDate:string
}

interface DocBuckets {
	currentWeek:ShareBaseDocument[],
	lastWeek:ShareBaseDocument[],
	older:ShareBaseDocument[]
}
		
/**
* @ngdoc service
* @name  sharebase-app.sbaRecentDocumentsDataStore
* 
* @description 
* A datastore used to interact with user's recent documents.
* 
**/
export function RecentDocumentsDataStoreFactory(
	sortContent,
	sbDocumentService:IDocumentService, 
	sbRecentDocumentsService
) {
	let _documents:DocBuckets = {
		currentWeek: [],
		lastWeek: [],
		older: []
	};

	return {
		/**
		* @ngdoc method
		* @name sbaRecentDocumentsDataStore#load
		* @methodOf sharebase-app.sbaRecentDocumentsDataStore
		* @kind function
		*
		* @description
		* Loads or refreshes a selected folder
		* 
		* @param {object} folderId A Folder ID
		*
		* @returns {Deferred} A promise that will resolve with a selected folder
		*/
		load,
		/**
		 * @ngdoc method
		 * @name sbaRecentDocumentsDataStore#renameDocument
		 * @methodOf sharebase-app.sbaRecentDocumentsDataStore
		 * @kind function
		 * 
		 * @description
		 * Renames a document
		 * 
		 * @param {ShareBaseDocument} document The document being renamed
		 * @param {string} newName The new name for the document
		 * 
		 * @returns {Deferred} A promise that will resolve with a document
		 */
		renameDocument,
		deleteDocument,
		clearStore,
		get hasDocuments() {
			return _documents.currentWeek.length > 0 ||
				_documents.lastWeek.length > 0 ||
				_documents.older.length > 0;
		},
		/**
		* @ngdoc property
		* @name sbaRecentDocumentsDataStore#documents
		* @propertyOf sharebase-app.sbaRecentDocumentsDataStore
		* @kind property
		*
		* @description
		* A user's recent documents
		* 
		* @returns {array} An array of recent documents
		*/
		get documents() { return _documents; }
	};

	function load() {
		_documents = angular.copy({
			currentWeek: [],
			lastWeek: [],
			older: []
		});
		return sbRecentDocumentsService
			.getDocuments()
			.then((recentDocs:RecentDocument[]) => {
				let allDocs = recentDocs.map(recent => {
					let doc = recent.Embedded.Document;
					// add the RecentView date value to Document object for the view;
					// TODO: remove this from the interface when we create a clientside document class
					doc.RecentView = new Date(recent.LastActivityDate);
					return doc;
				});

				return allDocs;
			})
			.then((allDocs:ShareBaseDocument[]) => {
				let oneWeekAgo = new Date();
				oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
				let twoWeeksAgo = new Date();
				twoWeeksAgo.setDate(twoWeeksAgo.getDate() - 14);
				
				allDocs.forEach(doc => {
					if (doc.RecentView > oneWeekAgo) {
						_documents.currentWeek.push(doc);
					}
					else if (doc.RecentView < oneWeekAgo && doc.RecentView > twoWeeksAgo) {
						_documents.lastWeek.push(doc);
					}
					else {
						_documents.older.push(doc);
					}
				});

				sort();

				return _documents;
			});
	}

	function sort() {
		// Recent documents should only be sorted from most recently viewed to oldest viewed
		_documents.currentWeek = sortContent(_documents.currentWeek, SortParameter[SortParameter.ViewDate], false);
		_documents.lastWeek = sortContent(_documents.lastWeek, SortParameter[SortParameter.ViewDate], false);
		_documents.older = sortContent(_documents.older, SortParameter[SortParameter.ViewDate], false);
	}

	function renameDocument(document:ShareBaseDocument, newName:string) {
		return sbDocumentService.renameDocument(document, newName)
			.then(function(renamedDocument) {
				angular.forEach(_documents, function(group) {
					group = group.map(function(doc) {
						if (doc.DocumentId === renamedDocument.DocumentId) {
							doc.DocumentName = renamedDocument.DocumentName;
							return angular.copy(doc);
						}
						else {
							return doc;
						}
					});
				});
			});
	}

	function deleteDocument(document:ShareBaseDocument, purge:boolean = false) {
		return sbDocumentService
			.deleteDocument(document, purge)
			.then(function(res) {
				angular.forEach(_documents, function(group) {
					group = group.filter(function(doc) {
						return doc.DocumentId !== document.DocumentId;
					});
				});
			});
	}

	function clearStore(){
		_documents.currentWeek = [];
		_documents.lastWeek = [];
		_documents.older = [];
	}
}

RecentDocumentsDataStoreFactory.$inject = [
	'sbSortContentFilter',
	'sbDocumentService',
	'sbRecentDocumentsService'
];


// WEBPACK FOOTER //
// ./src/sharebase-app/dataStores/recentDocumentsDataStore.ts